import { z } from "zod";
import type { AuditAction } from "../audit/actions";
import { cursorQuerySchema } from "./common";

/**
 * The seven saved review reports of SEC-16 (§164.308(a)(1)(ii)(D)).
 *
 * They are a closed catalog, not a free-form action filter, for two reasons. A reviewer's
 * evidence has to be reproducible — "the grants report for March" must mean the same set of
 * actions to the auditor as it did to the reviewer — and a report id in the audit row is worth
 * more than a query string when somebody later asks what was reviewed.
 *
 * Reading a report is itself audited as `audit.review` and exporting one as `history.export`,
 * because the review is the control the requirement asks for evidence of.
 */

export const AUDIT_REPORT_IDS = [
  "sign-ins",
  "denials",
  "grants",
  "destination-changes",
  "secret-writes",
  "force-resets",
  "exports",
] as const;
export type AuditReportId = (typeof AUDIT_REPORT_IDS)[number];

export interface AuditReportSpec {
  id: AuditReportId;
  label: string;
  /** The closed set of actions the report covers. */
  actions: readonly AuditAction[];
  /**
   * SEC-18: the grants report lists break-glass rows first, so a self-grant cannot be missed
   * by a reviewer who only reads the first page.
   */
  breakGlassFirst?: boolean;
}

export const AUDIT_REPORTS: Record<AuditReportId, AuditReportSpec> = {
  "sign-ins": {
    id: "sign-ins",
    label: "Sign-ins and failures",
    actions: [
      "auth.sign_in",
      "auth.sign_in_failed",
      "auth.sign_out",
      "auth.token_rejected",
      "auth.reauth",
      "auth.mfa_device_remember",
      "auth.mfa_device_forget",
    ],
  },
  denials: {
    id: "denials",
    label: "Permission denials",
    actions: ["auth.denied"],
  },
  grants: {
    id: "grants",
    label: "Access grants and revocations",
    actions: [
      "access.grant",
      "access.revoke",
      "access.reactivate",
      "access.invite_resend",
      "user.create",
      "user.activate",
      "user.deactivate",
      // #1639 C1: the email sign-in account a grant asked for, as the runner closed it.
      "account.create",
      // Group access (2026-09-28): access that follows the customer's Entra groups.
      "group_mapping.create",
      "group_mapping.remove",
      "access.group_grant",
      "access.group_end",
      "access.group_override_set",
      "access.group_enforcement_exempt",
      "tenant.group_enforcement_update",
    ],
    breakGlassFirst: true,
  },
  "destination-changes": {
    id: "destination-changes",
    label: "Destination changes",
    actions: ["binding.update", "binding.read"],
  },
  "secret-writes": {
    id: "secret-writes",
    label: "Secret writes",
    actions: ["secret.write", "secret.delete"],
  },
  "force-resets": {
    id: "force-resets",
    label: "Force resets",
    actions: ["run.force_reset"],
  },
  exports: {
    id: "exports",
    label: "Exports",
    actions: ["audit.export", "history.export"],
  },
};

export const auditReportParamsSchema = z.object({ name: z.enum(AUDIT_REPORT_IDS) });
export type AuditReportParams = z.infer<typeof auditReportParamsSchema>;

/**
 * A report is a date range. `action` is deliberately absent: the report id decides the action
 * set, and a caller who wants one action uses `/audit/events`. `actor` is absent too — like
 * the events query it travels in the `x-skydata-audit-actor` header (SEC-13, see
 * `AUDIT_ACTOR_HEADER`), so zod strips an `?actor=` left over from a bookmark or an earlier
 * release and the reviewer gets an unfiltered page rather than a 400 they cannot act on.
 */
export const auditReportQuerySchema = cursorQuerySchema.extend({
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
});
export type AuditReportQuery = z.infer<typeof auditReportQuerySchema>;

export const auditReportRowSchema = z.object({
  id: z.string(),
  occurredAt: z.string(),
  action: z.string(),
  actorUserId: z.uuid().nullable(),
  actorRef: z.string(),
  /**
   * #1090: the person's address, joined from platform `users` when the page is read, exactly as
   * All events joins it (`actorEmail` on the trail's rows), so a report names the same person as
   * the trail for every reader. Null for a system actor or a person no longer in `users`. The row
   * stores only the id and the keyed hash (SEC-13), and the report CSV carries no address.
   */
  actorEmail: z.string().nullable(),
  actorType: z.string(),
  entityType: z.string(),
  entityId: z.string().nullable(),
  /** Already a display-safe form when it refers to a person (SEC-13). */
  entityLabel: z.string().nullable(),
  correlationId: z.string(),
  scopeType: z.string(),
  tenantId: z.uuid().nullable(),
  instanceId: z.uuid().nullable(),
  /** SEC-18: true when the grantor granted themselves. */
  breakGlass: z.boolean(),
  reason: z.string().nullable(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  route: z.string().nullable(),
  /**
   * #1373: a refused attempt (the audit capture's `outcome: "failed"` with the refusal's
   * `statusCode`, `isRefusedAttemptMetadata`), read the way All events reads it, so a report
   * never shows a refused grant as a grant. Nothing durable changed on such a row.
   */
  failed: z.boolean(),
  /**
   * #1373: `metadata.section` when the row names one (#1248: a dbt repository save audited as
   * `binding.update` carries `transform_source`), so a refused save reads with the same words as
   * in All events. Null otherwise.
   */
  section: z.string().nullable(),
});
export type AuditReportRow = z.infer<typeof auditReportRowSchema>;

export const auditReportViewSchema = z.object({
  report: z.enum(AUDIT_REPORT_IDS),
  label: z.string(),
  actions: z.array(z.string()),
  from: z.string().nullable(),
  to: z.string().nullable(),
  rows: z.array(auditReportRowSchema),
});
export type AuditReportView = z.infer<typeof auditReportViewSchema>;

/** The CSV header, in column order. Mirrors the row view minus the free-form metadata. */
export const AUDIT_REPORT_CSV_COLUMNS = [
  "occurred_at",
  "action",
  "break_glass",
  "entity_type",
  "entity_id",
  "entity_label",
  "actor_user_id",
  "actor_ref",
  "actor_type",
  "correlation_id",
  "scope_type",
  "tenant_id",
  "instance_id",
  "reason",
  "ip",
  "route",
  // #1373: `failed` for a failed or refused attempt (a capture's refused attempt, or an action
  // that is a failure by definition, such as a permission denial), else empty
  // (`auditOutcomeCell`, `shared/audit/refusedAttempt.ts`). Last, so a reader that takes the
  // columns by position before it still reads them in the same place.
  "outcome",
] as const;
